/** Shared Custom Request (per-app authenticated HTTP) for agents — mirrors workflow ActionPanel. */

export const CUSTOM_REQUEST_ACTION_KEY = "Custom Request";

export const CUSTOM_REQUEST_HTTP_FIELD_NAME = "HTTP Request";

export const CUSTOM_REQUEST_DEFAULT_VALUE = {
  requestType: "GET",
  url: "",
  params: [{ key: "", value: "" }],
  headers: [{ key: "", value: "" }],
  stringBody: "",
};

/** RJSF-style field used by Custom Request (host app auth applies). */
export const CUSTOM_REQUEST_FIELDS = [
  {
    name: CUSTOM_REQUEST_HTTP_FIELD_NAME,
    title: "HTTP Request",
    description:
      "Authenticated call via this app's connector. Prefer a native catalog action when one exists. Use Workload Code for transforms — not Custom Request.",
    in: "path",
    required: true,
    schema: {
      type: "object",
      title: "HTTP Request",
      properties: {
        requestType: {
          title: "Request Type",
          type: "string",
          enum: ["GET", "POST", "PUT", "PATCH", "DELETE"],
        },
        url: {
          title: "URL",
          type: "string",
          description:
            "Absolute API endpoint starting with https:// (include host). Never path-only values like /invoices.",
        },
        params: {
          title: "URL Parameters",
          type: "array",
          items: {
            type: "object",
            properties: {
              key: { title: "Key", type: "string", noDataPicker: true },
              value: { title: "Value", type: "string" },
            },
          },
          default: [{ key: "", value: "" }],
        },
        headers: {
          title: "Headers",
          description: "Optional. Do not invent auth headers — connector auth handles that.",
          type: "array",
          items: {
            type: "object",
            properties: {
              key: { title: "Key", type: "string", noDataPicker: true },
              value: { title: "Value", type: "string" },
            },
          },
          default: [{ key: "", value: "" }],
        },
        stringBody: {
          type: "string",
          title: "JSON Body",
          description: "Ignored for GET requests. Use JSON text; leave dynamic values for the agent when needed.",
        },
      },
      required: ["requestType", "url"],
    },
  },
];

export const CUSTOM_REQUEST_ACTION = {
  key: CUSTOM_REQUEST_ACTION_KEY,
  title: "Custom Request",
  description:
    "Call this app's API when no native action exists. Requires connector auth and API docs. Not for transforms — use Workload Code for those.",
  customForm: { custom: true },
  fields: CUSTOM_REQUEST_FIELDS,
  dynamicForm: null,
  body: "{}",
  url_params: "{}",
  method: "",
  url: "",
};

/** True when catalog app expects a credential (not Utility / auth:false). */
export const appSupportsCustomRequest = (appMeta) => {
  if (!appMeta || typeof appMeta !== "object") return false;
  const slug = String(appMeta.app || appMeta.slug || "").toLowerCase().trim();
  if (!slug || slug === "schedule" || slug === "workload-code" || slug === "workload-browser") return false;
  const cat = String(appMeta.category || "").toLowerCase();
  if (/\butility\b|\butilities\b/.test(cat)) return false;
  // Phase C may draft auth while catalog still says auth:false
  if (appMeta.needsCredential === true || appMeta.authDrafted === true) return true;
  // disableAuth = Passport auth already on server-master (not "no auth")
  const disableAuth =
    appMeta.disableAuth === true ||
    appMeta.disableAuth === 1 ||
    String(appMeta.disableAuth || "").toLowerCase() === "true";
  if (disableAuth) return true;
  if (appMeta.auth === false || appMeta.auth === "false" || appMeta.auth === 0) {
    // Still allow Custom Request connect when docs exist (credential required at runtime)
    if (String(appMeta.apiDocs || appMeta.apiDocsUrl || "").trim()) return true;
    return false;
  }
  // Prefer apps that look authenticated / have passport / explicit auth true.
  if (appMeta.auth === true || appMeta.auth === "true" || appMeta.auth === 1) return true;
  if (Array.isArray(appMeta.auths) && appMeta.auths.length > 0) return true;
  if (appMeta.passportApp) return true;
  // Many catalog rows omit auth but still need OAuth — allow Custom Request for non-utility action apps.
  if (appMeta.action === true || appMeta.action === "true") return true;
  return Boolean(slug);
};

/** Non-empty apiDocs URL (or text) from allApps. */
export const appHasApiDocs = (appMeta) => {
  const docs = appMeta?.apiDocs ?? appMeta?.apiDocsUrl ?? "";
  return String(docs || "").trim().length > 0;
};

export const isAbsoluteHttpUrl = (url) => /^https?:\/\//i.test(String(url || "").trim());

/** Origin (scheme://host) from an absolute URL. */
export const originFromAbsoluteUrl = (url) => {
  try {
    const u = new URL(String(url || "").trim());
    if (!/^https?:$/i.test(u.protocol) || !u.host) return "";
    return `${u.protocol}//${u.host}`.replace(/\/$/, "");
  } catch {
    return "";
  }
};

/**
 * Priority 1: derive verified API bases from sibling native action absolute urls
 * in a formData action map (skip Custom Request itself).
 */
export const deriveVerifiedBasesFromActionMap = (actionMap = {}) => {
  const bases = [];
  const absoluteUrls = [];
  const seen = new Set();
  const map =
    actionMap && typeof actionMap === "object" && !Array.isArray(actionMap) ? actionMap : {};
  Object.keys(map).forEach((key) => {
    const low = String(key || "").toLowerCase();
    if (low === "custom request" || low === "customrequest" || low === "workload_http") return;
    const action = map[key];
    const url = String(action?.url || "").trim();
    if (!isAbsoluteHttpUrl(url)) return;
    absoluteUrls.push(url);
    const origin = originFromAbsoluteUrl(url);
    if (origin && !seen.has(origin.toLowerCase())) {
      seen.add(origin.toLowerCase());
      bases.push(origin);
    }
  });
  return { bases, absoluteUrls, source: bases.length ? "sibling_actions" : "" };
};

/**
 * Join relative path with a verified base only — never invent hosts from docs URLs.
 * @param {string} url
 * @param {{ apiBaseUrl?: string, verifiedApiBase?: string, verifiedBases?: string[] }} opts
 */
export const resolveAbsoluteApiUrl = (url, opts = {}) => {
  const u = String(url || "").trim();
  if (!u || u === "Let agent pick value") return u;
  if (isAbsoluteHttpUrl(u)) return u;
  const path = u.startsWith("/") ? u : `/${u}`;
  const bases = [
    opts.apiBaseUrl,
    opts.verifiedApiBase,
    ...(Array.isArray(opts.verifiedBases) ? opts.verifiedBases : []),
  ]
    .map((b) => String(b || "").trim().replace(/\/$/, ""))
    .filter((b) => isAbsoluteHttpUrl(b));
  if (!bases.length) return u; // fail closed
  return `${bases[0]}${path}`;
};

export const normalizeCustomRequestHttpValue = (value, opts = {}) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  return {
    ...value,
    url: resolveAbsoluteApiUrl(value.url, opts),
  };
};

/**
 * Shape HTTP Request.value to match workflow executeAction (object, not stringified).
 * Working sample:
 *   { requestType: "POST", url: "https://…", stringBody: "{\"a\":1}" }
 * Omits empty params/headers; ensures stringBody is a JSON string when present.
 */
export const shapeCustomRequestHttpValueForExecute = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const out = {};
  const requestType = String(value.requestType || "GET").toUpperCase();
  out.requestType = ["GET", "POST", "PUT", "PATCH", "DELETE"].includes(requestType)
    ? requestType
    : "GET";
  if (value.url != null && String(value.url).trim() !== "") {
    out.url = String(value.url).trim();
  }
  if (Array.isArray(value.params)) {
    const params = value.params
      .filter((row) => row && String(row.key || "").trim())
      .map((row) => ({
        key: String(row.key).trim(),
        value: row.value == null ? "" : String(row.value),
      }));
    if (params.length) out.params = params;
  }
  if (value.stringBody != null && value.stringBody !== "") {
    if (typeof value.stringBody === "object") {
      try {
        out.stringBody = JSON.stringify(value.stringBody);
      } catch {
        out.stringBody = String(value.stringBody);
      }
    } else {
      out.stringBody = String(value.stringBody);
    }
  }
  if (Array.isArray(value.headers)) {
    const headers = value.headers
      .filter((row) => row && String(row.key || "").trim())
      .map((row) => ({
        key: String(row.key).trim(),
        value: row.value == null ? "" : String(row.value),
      }));
    if (headers.length) out.headers = headers;
  }
  return out;
};

/**
 * Ensure Custom Request appears in an actions map from formData.
 * @returns {{ [key: string]: object }}
 */
export const mergeCustomRequestIntoActionMap = (actionMap = {}, appMeta = null) => {
  const map =
    actionMap && typeof actionMap === "object" && !Array.isArray(actionMap)
      ? { ...actionMap }
      : {};
  if (appMeta && !appSupportsCustomRequest(appMeta)) return map;
  const existingKey = Object.keys(map).find(
    (k) => String(k).toLowerCase() === "custom request" || String(k).toLowerCase() === "customrequest"
  );
  if (existingKey) return map;
  map[CUSTOM_REQUEST_ACTION_KEY] = { ...CUSTOM_REQUEST_ACTION };
  return map;
};

export const isCustomRequestAction = (action = {}) => {
  const key = String(
    action.action_key || action.config?.action || action.type || action.key || ""
  )
    .trim()
    .toLowerCase();
  return key === "custom request" || key === "customrequest" || key === "workload_http";
};
